import { ThemeColor } from '@/enums'
import { MailIcon } from '@/icons'
import { returns, useModal } from '@/utils'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { Typography } from '../../atoms'
import { ShowWhen } from '../../hoc/ShowWhen'
import { Row } from '../../styled'
import { ContactModal } from '../ContactModal'
import { Container, Header, StyledImage } from './styles'
import { Button, ButtonProps } from '@surein/ui'

const useMessages = () => {
  const { t } = useTranslation()

  return {
    buttonText: t('services.contacts'),
    fromYourAdvisor: t('fromYourAdvisor'),
    advisorText: t('question_advisor_text'),
  }
}

interface SecondaryButtonProps extends ButtonProps {
  children: ReactNode
}

const SecondaryButton = ({ children, ...props }: SecondaryButtonProps) => (
  <Button {...props} variant="muted">
    {children}
  </Button>
)

interface OutletContextType {
  hasHeadquarters: boolean
}

export interface ContactExpertProps {
  title?: string
  text?: string
  reverted?: boolean
  defaultButton?: React.ComponentType<
    ButtonProps & SecondaryButtonProps & React.RefAttributes<HTMLButtonElement>
  >
  buttonComponent?: React.ReactNode
  noMobilePadding?: boolean
  buttonText?: string
  textColor?: string
}

export const ContactExpert = ({
  title,
  text,
  reverted,
  defaultButton: DefaultButton = SecondaryButton,
  buttonComponent: ButtonComponent,
  noMobilePadding,
  buttonText,
  textColor = ThemeColor.b100,
}: ContactExpertProps) => {
  const messages = useMessages()
  const { isOpen, open, close } = useModal()
  const { hasHeadquarters } = useOutletContext<OutletContextType>()

  return (
    <Container noMobilePadding={noMobilePadding}>
      <Header reverted={reverted}>
        <Row alignItems="center" gap="4px">
          <MailIcon fill="none" />
          <Typography color={ThemeColor.b50} variant="p2Body">
            {title || messages.fromYourAdvisor}
          </Typography>
        </Row>
        <StyledImage name="chatblock" type="png" />
      </Header>
      <Typography color={textColor} variant="p1Body">
        {text || messages.advisorText}
      </Typography>
      <ShowWhen fallback={returns(ButtonComponent)} when={!ButtonComponent}>
        <DefaultButton onClick={open} shape="square">
          {buttonText || messages.buttonText}
        </DefaultButton>
      </ShowWhen>
      {isOpen && (
        <ContactModal
          hasHeadquarters={hasHeadquarters}
          isOpen={isOpen}
          onClose={close}
        />
      )}
    </Container>
  )
}
